import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { CheckCircle, Zap, Crown, Building } from 'lucide-react';

const plans = [
  {
    name: 'Free',
    price: '$0',
    period: '/month',
    icon: Zap,
    color: 'text-gray-400',
    border: 'border-[#1a2235]',
    features: ['10 wallet scans/day', '1 API key', 'Basic risk scoring', 'Scan history (30 days)', 'Community support'],
    cta: 'Get Started Free',
    to: '/register',
  },
  {
    name: 'Starter',
    price: '$19',
    period: '/month',
    icon: Zap,
    color: 'text-[#00E5FF]',
    border: 'border-[#00E5FF]/40',
    highlight: true,
    features: ['200 scans/day', '3 API keys', 'Advanced risk analysis', 'PDF report exports', 'Scan history (1 year)', 'Email support'],
    cta: 'Start Starter',
    to: '/register',
  },
  {
    name: 'Pro',
    price: '$49',
    period: '/month',
    icon: Crown,
    color: 'text-[#7C3AED]',
    border: 'border-[#7C3AED]/30',
    features: ['1,000 scans/day', '10 API keys', 'All Starter features', 'Team access (5 seats)', 'Webhook alerts', 'Bulk wallet scanning', 'Priority support'],
    cta: 'Start Pro',
    to: '/register',
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    icon: Building,
    color: 'text-amber-400',
    border: 'border-amber-500/30',
    features: ['Unlimited scans', '50+ API keys', 'All Pro features', 'Unlimited team seats', 'SLA guarantee', 'Dedicated account manager', 'On-premise deployment'],
    cta: 'Contact Sales',
    to: '/contact',
  },
];

export default function Pricing() {
  return (
    <div className="min-h-screen bg-[#080c16] text-white">
      <Navbar />
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-black mb-4">
              Simple, <span className="gradient-text">transparent</span> pricing
            </h1>
            <p className="text-gray-400 text-xl max-w-2xl mx-auto">
              Start free. Upgrade when you need more power. No hidden fees.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {plans.map(({ name, price, period, icon: Icon, color, border, highlight, features, cta, to }) => (
              <div
                key={name}
                className={`glass-card p-6 flex flex-col border-2 ${border} ${highlight ? 'relative' : ''}`}
              >
                {highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00E5FF] text-black text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                    MOST POPULAR
                  </div>
                )}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className={`w-5 h-5 ${color}`} />
                    <h3 className="text-white font-bold text-lg">{name}</h3>
                  </div>
                  <p className={`text-4xl font-black ${color}`}>
                    {price}
                    <span className="text-gray-500 text-base font-normal">{period}</span>
                  </p>
                </div>

                <ul className="space-y-3 flex-1 mb-8">
                  {features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-gray-400">
                      <CheckCircle className="w-4 h-4 text-[#00E5FF] flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>

                <Link
                  to={to}
                  className={`w-full py-3 text-center font-bold rounded-xl text-sm transition-all block ${
                    highlight
                      ? 'bg-gradient-to-r from-[#00E5FF] to-[#7C3AED] text-black hover:opacity-90'
                      : `border ${border} ${color} hover:bg-white/5`
                  }`}
                >
                  {cta}
                </Link>
              </div>
            ))}
          </div>

          {/* FAQ */}
          <div className="mt-20 max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-white text-center mb-10">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {[
                { q: 'Is the free plan really free?', a: 'Yes! The free plan includes 10 wallet scans per day with no credit card required.' },
                { q: 'Can I cancel anytime?', a: 'Absolutely. You can cancel your subscription at any time from the billing settings. No penalties.' },
                { q: 'What chains do you support?', a: 'We currently support Ethereum mainnet. BSC, Polygon, and Arbitrum are coming soon.' },
                { q: 'Is my data private?', a: 'Yes. Wallet addresses you scan are not shared or sold. See our Privacy Policy for details.' },
              ].map(({ q, a }) => (
                <div key={q} className="glass-card p-6">
                  <h4 className="text-white font-semibold mb-2">{q}</h4>
                  <p className="text-gray-400 text-sm">{a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
